(function () {
  'use strict';

  /* ========================================================================
     TMC Attribution — first/last touch capture → cart attributes
     Writes two cookies from UTM params / external referrers, then syncs them
     onto the cart so they land on the order. Skio replays cart attributes
     onto subscription renewals, so this also attributes recurring revenue.
     Fails silently everywhere: attribution must never break the storefront.
     ======================================================================== */

  var FIRST_COOKIE = 'tmc_attr_first';
  var LAST_COOKIE = 'tmc_attr_last';
  var SYNC_FLAG = 'tmc_attr_synced';
  var FIRST_DAYS = 365;
  var LAST_DAYS = 30;
  var MAX_LEN = 100;
  var CLICK_ID_MAX_LEN = 255;
  var MAX_COOKIE_BYTES = 1500;

  var UTM_KEYS = ['source', 'medium', 'campaign', 'content', 'term'];

  var TOUCH_FIELDS = [
    'source',
    'medium',
    'campaign',
    'content',
    'term',
    'landing',
    'referrer',
    'ts',
    'click_id',
    'click_source',
  ];

  // Click IDs that reliably indicate a PAID click, and may therefore derive
  // source/medium. gclid is appended only by Google Ads and msclkid only by
  // Microsoft Ads. fbclid and ttclid are deliberately absent: Facebook and TikTok
  // append them to organic links too, so inferring paid from them misclassified
  // organic Instagram traffic as Paid Search.
  var PAID_CLICK_IDS = { gclid: 'google', msclkid: 'bing' };

  // Every click ID worth STORING as a raw value, for Google Ads offline conversion
  // import and Meta CAPI matching. Order matters — the first one found wins, since
  // a visit realistically arrives from one platform. Storing the raw value is safe
  // for classification because the webhook's hasUtm() ignores click_id entirely.
  var CAPTURE_CLICK_IDS = [
    { param: 'gclid', platform: 'google' },
    { param: 'fbclid', platform: 'facebook' },
    { param: 'ttclid', platform: 'tiktok' },
    { param: 'msclkid', platform: 'bing' },
  ];

  function readCookie(name) {
    try {
      var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
      return m ? JSON.parse(decodeURIComponent(m[2])) : null;
    } catch (e) {
      return null;
    }
  }

  function writeCookie(name, value, days) {
    try {
      var encoded = encodeURIComponent(JSON.stringify(value));
      if (encoded.length > MAX_COOKIE_BYTES) return;
      var expires = new Date(Date.now() + days * 86400000).toUTCString();
      document.cookie = name + '=' + encoded + ';expires=' + expires + ';path=/;SameSite=Lax;Secure';
    } catch (e) {}
  }

  function cap(value, limit) {
    if (value === null || value === undefined) return null;
    var s = String(value).trim();
    // Mirrors the webhook's `FIELD_MAX_LEN[f] ?? MAX_LEN` (commit 74ed6cb):
    // `||` would treat an explicit 0 override as falsy and fall back to
    // MAX_LEN, silently ignoring it. This file stays ES5, so check the type
    // instead of using `??`.
    return s ? s.slice(0, typeof limit === 'number' ? limit : MAX_LEN) : null;
  }

  // Builds a touch from the current page, or null for plain internal navigation.
  function buildTouch() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return null;
    }

    var touch = {};
    TOUCH_FIELDS.forEach(function (f) {
      touch[f] = null;
    });

    var hasCampaign = false;
    UTM_KEYS.forEach(function (k) {
      var v = cap(params.get('utm_' + k));
      if (v) {
        touch[k] = v.toLowerCase();
        hasCampaign = true;
      }
    });

    // 1. Paid inference — only from unambiguously-paid click IDs.
    if (!hasCampaign) {
      Object.keys(PAID_CLICK_IDS).some(function (id) {
        if (!params.get(id)) return false;
        touch.source = PAID_CLICK_IDS[id];
        touch.medium = 'cpc';
        hasCampaign = true;
        return true;
      });
    }

    // 2. Raw capture — independent of inference, and deliberately does NOT set
    // hasCampaign. A click ID alone must not make this look campaign-tagged;
    // an external referrer or a real UTM is what qualifies a touch.
    CAPTURE_CLICK_IDS.some(function (entry) {
      var raw = params.get(entry.param);
      if (!raw) return false;
      touch.click_id = cap(raw, CLICK_ID_MAX_LEN);
      touch.click_source = entry.platform;
      return true;
    });

    var referrer = null;
    try {
      if (document.referrer) {
        var host = new URL(document.referrer).hostname.toLowerCase();
        if (host && host !== window.location.hostname) referrer = cap(host);
      }
    } catch (e) {}

    if (!hasCampaign && !referrer) return null;

    touch.referrer = referrer;
    touch.landing = cap(window.location.pathname);
    touch.ts = new Date().toISOString();
    return touch;
  }

  var touch = buildTouch();
  if (touch) {
    if (!readCookie(FIRST_COOKIE)) writeCookie(FIRST_COOKIE, touch, FIRST_DAYS);
    writeCookie(LAST_COOKIE, touch, LAST_DAYS);
  }

  function buildAttributes() {
    var cookies = { first: readCookie(FIRST_COOKIE), last: readCookie(LAST_COOKIE) };
    var attrs = {};
    var found = false;

    ['first', 'last'].forEach(function (which) {
      var t = cookies[which];
      if (!t) return;
      TOUCH_FIELDS.forEach(function (f) {
        if (t[f]) {
          attrs['_tmc_' + which + '_' + f] = String(t[f]);
          found = true;
        }
      });
    });

    if (!found) return null;
    attrs._tmc_synced = '1';
    return attrs;
  }

  var syncing = false;

  function syncToCart() {
    try {
      if (syncing) return;
      var attrs = buildAttributes();
      if (!attrs) return;
      syncing = true;

      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
      })
        .then(function (response) {
          if (response.ok) {
            try {
              sessionStorage.setItem(SYNC_FLAG, '1');
            } catch (e) {}
          }
        })
        .catch(function () {})
        .finally(function () {
          syncing = false;
        });
    } catch (e) {
      syncing = false;
    }
  }

  // The theme publishes cartUpdate on add-to-cart and quantity changes.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      syncToCart();
    });
  }

  // Page-load reconcile: covers a cart that already has items from an earlier
  // session. Runs at most once per session; later changes ride on cartUpdate.
  try {
    if (!sessionStorage.getItem(SYNC_FLAG) && buildAttributes()) {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          var needsSync = cart && cart.item_count > 0 && !(cart.attributes && cart.attributes._tmc_synced);
          if (needsSync) {
            syncToCart();
          } else {
            try {
              sessionStorage.setItem(SYNC_FLAG, '1');
            } catch (e) {}
          }
        })
        .catch(function () {});
    }
  } catch (e) {}
})();
